import { SelectedRates } from '~/reducers/rates/rates.reducer';
import { createSelector } from 'reselect';
import { getAllServiceLevelsMapByAccount } from '~/selectors/providers.selectors';
import Rate from '~/types/Rate';
import { ApplicationState } from '../store';

export const getRatesStore = (state: ApplicationState) => state.rates;

export const getRatesData = createSelector(
  getRatesStore,
  (ratesStore): HashMap<string, Rate> => ratesStore.data,
);

// returns rates data filtered to remove rates with service levels which the user has hidden
export const getActiveRates = createSelector(
  getRatesStore,
  getAllServiceLevelsMapByAccount,
  (ratesStore, activeServiceLevels): HashMap<string, Rate> => {
    // fall back to returning all rates if service level metadata is unavailable for some reason
    if (!activeServiceLevels || !Object.keys(activeServiceLevels).length) {
      return ratesStore.data;
    }

    const activeRates = {};
    Object.entries(ratesStore.data).forEach(([key, rate]) => {
      if (
        // filter out explicitly hidden service levels; default to including a rate if it can't be determined
        // whether the rate is hidden or not
        activeServiceLevels?.[rate.carrierAccount]?.serviceLevelsByToken[
          rate.servicelevel.extended_token ?? rate.servicelevel.token
        ]?.is_hidden !== true
      ) {
        activeRates[key] = rate;
      }
    });
    return activeRates;
  },
);

export const getRatesIdsByShipmentId = createSelector(
  getRatesStore,
  getActiveRates,
  (ratesStore, activeRates): HashMap<string, readonly string[]> => {
    const ratesIdsByShipmentId = {};
    Object.entries(ratesStore.byShipmentId).forEach(([key, rateIds]) => {
      // filter out rates with service levels that have been hidden by the user
      ratesIdsByShipmentId[key] = rateIds.filter((rateId) => activeRates[rateId]);
    });
    return ratesIdsByShipmentId;
  },
);

export const getUniqueServiceLevels = createSelector(getActiveRates, (ratesData) =>
  Object.keys(ratesData).reduce((uniqueServiceLevels, rateObjectId) => {
    const rate = ratesData[rateObjectId];
    const serviceLevel = rate.servicelevel;

    return uniqueServiceLevels[serviceLevel.token]
      ? uniqueServiceLevels
      : {
          ...uniqueServiceLevels,
          [serviceLevel.token]: { ...serviceLevel, provider: rate.provider },
        };
  }, {}),
);

export const getSelectedRates = createSelector(
  getRatesStore,
  getActiveRates,
  (ratesStore, activeRates): SelectedRates => {
    const selectedRateIds = {};
    Object.entries(ratesStore.selectedRates).forEach(([key, rateId]) => {
      // filter out rates with service levels that have been hidden by the user
      if (activeRates[rateId]) {
        selectedRateIds[key] = rateId;
      }
    });
    return selectedRateIds;
  },
);

export const getUserTouchedRates = createSelector(
  getRatesStore,
  (ratesStore) => ratesStore.userTouchedRates,
);

export const getSelectedRatesData = createSelector(
  getActiveRates,
  getSelectedRates,
  (activeRates, batchSelectedRates): Rate[] =>
    // filter out rates with service levels that have been hidden by the user
    Object.values(batchSelectedRates)
      .map((selectedRate) => activeRates[selectedRate])
      .filter((rate) => rate),
);
